// stahlia-frontend/src/api/inventur.ts
// API-Layer Inventur-Modul (Spec-Merkliste ③). Pfade relativ zur BASE '/api' (client.ts).
// Typen sind Drizzle-camelCase-Spiegel der Backend-Response (src/modules/warehouse/inventur.ts),
// analog zu WarehousePlace in api/warehouse.ts.
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiFetch, apiUpload } from './client'
import { newIdempotencyKey } from '@/lib/offline-queue'
import type { InventurMode, InventurScope } from '@/lib/inventur-form'
import { useWarehouseCompanyScope } from '@/lib/warehouse-company'

export interface InventurSession {
  id: number
  mode: InventurMode
  scope: InventurScope
  status: 'offen' | 'abgeschlossen'
  note: string | null
  createdBy: number | null
  createdByName: string | null
  closedBy: number | null
  closedByName: string | null
  closedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface InventurPlace {
  id: number; barcode: string; zone: 'fuss' | 'stapler'
  rowCode: string; fieldNo: number; slotNo: number; levelNo: number
}

export interface InventurCountRow {
  id: number; placeId: number | null; positionId: number | null; stockItemId: number | null
  merchantSku: string | null; countType: string
  expectedQty: number | null; countedQty: number | null
  cartons?: number | null; perCarton?: number | null; netzeRollLengthM?: number | null
  expectedState: string | null
  isRecount: boolean; countedByName: string | null; deviceLabel: string | null
  note: string | null; createdAt: string; placeBarcode: string | null
}

/** Raw count fields returned by analyse (detail projects fewer columns). */
export interface AnalyseCount extends Omit<InventurCountRow, 'placeBarcode'> {
  sessionId: number; countedBy: number | null; idempotencyKey: string | null
}
export type PaletteStatus = 'offen' | 'bestaetigt' | 'abweichung' | 'nachzaehlung_angefordert' | 'nachgezaehlt'
  | 'nicht_gefunden' | 'soll_unbekannt' | 'nicht_zaehlbar' | 'gebucht' | 'verworfen' | 'klaerfall'
export interface InventurDecision {
  id: number; sessionId: number; countId: number | null; positionId: number | null; handregalMerchantSku: string | null
  ziel: 'palette' | 'handregal'; entscheidung: 'gebucht' | 'unveraendert' | 'verworfen' | 'klaerfall' | 'veraltet'
  qtyFrom: number | null; qtyTo: number | null; moveId: number | null; decidedBy: number | null
  decidedByName: string | null; deviceLabel: string | null; createdAt: string
}
export interface InventurFreigabeExpected {
  palette: Array<{ position_id: number; count_id: number | null }>
  handregal_count_id?: number | null
}
export interface AnalysePalette {
  positionId: number; placeId: number | null; placeBarcode: string | null; rowCode: string | null; paletteNo: string | null
  itemName: string | null; state: string; einheit: 'm' | 'stueck'; status: PaletteStatus
  netzeRollLengthM?: number | null
  soll: number | null; ist: number | null; differenz: number | null; offen: boolean; pending: boolean
  effectiveCount: AnalyseCount | null; firstCount: AnalyseCount | null; secondCount: AnalyseCount | null
}
export interface AnalyseArtikel {
  merchantSku: string; itemName: string | null; soll: number | null; ist: number | null; differenz: number | null
  status: 'kein_bestand' | 'offen' | 'klaerfall' | 'verworfen' | 'gebucht' | 'soll_unbekannt' | 'bestaetigt' | 'abweichung'
  vollstaendig: boolean; paletten: AnalysePalette[]
  handregal: { soll: number | null; ist: number | null; count: AnalyseCount | null; entscheidung: InventurDecision | null } | null
  drInfo: { drStand: number | null; offeneVerkaufsmengen: number; vergleichswert: number | null; echteDifferenz: number | null }
}
export interface InventurAnalyse {
  session: InventurSession; artikel: AnalyseArtikel[]
  kennzahlen: { artikelGesamt: number; artikelGezaehlt: number; artikelOffen: number; artikelAbweichung: number; plusStueck: number; minusStueck: number }
  zaehlprotokoll: Array<AnalyseCount & { placeBarcode: string | null; rowCode: string | null }>
  offenePlaetze: InventurPlace[]; entscheidungen: InventurDecision[]
  entscheidungsprotokoll: Array<{ id: number; eventType: string; severity: string; positionId: number | null; placeId: number | null;
    receiptId: number | null; moveId: number | null; message: string; status: string; resolvedBy: number | null;
    resolvedAt: string | null; resolutionNote: string | null; eventId: string | null; createdAt: string }>
}
export interface ImportPosition {
  id: number; labelId: number | null; placeId: number | null; state: string; stockItemId: number | null; merchantSku: string | null
  itemName: string | null; rawCode: string | null; freeText: string | null; qtyExact: number | null; qtyCirca: number | null
  piecesPerCarton: number | null; netzeRollLengthM: number | null; netzeRollsPerCarton: number | null; netzeUnitsVersion: string | null
  cartons: number | null; restPieces: number | null; firstInAt: string | null; isMisch: boolean; pickedUpBy: number | null
  pickedUpDevice: string | null; pickedUpWorkerId: number | null; pickedUpWorkerName: string | null; note: string | null; createdAt: string; updatedAt: string
}
export interface InventurImportPreview {
  merchant_skus: string[]
  rows: Array<{ input: string; status: 'ok' | 'kein_bestand' | 'unbekannt' | 'mehrdeutig'; merchant_sku: string | null; duplicate: boolean
    paletten: ImportPosition[]; plaetze: InventurPlace[]; handregal: { id: number; merchantSku: string; itemName: string | null; qty: number;
      firstCountedAt: string; countedAt: string; countedByWorkerId: number | null; countedByWorkerName: string | null;
      createdAt: string; updatedAt: string; korrigiertAt: string | null } | null }>
}
export function useInventurAnalyse(id: number | null) {
  const scope = useWarehouseCompanyScope()
  return useQuery({ queryKey: ['inventur', 'analyse', id, scope.key], enabled: id !== null,
    queryFn: () => apiFetch<InventurAnalyse>(`/warehouse/inventur/${id}/analyse/`), refetchInterval: 15_000 })
}
function invalidateInventur(qc: QueryClient, id: number) {
  void qc.invalidateQueries({ queryKey: detailKey(id) })
  void qc.invalidateQueries({ queryKey: ['inventur', 'analyse', id] })
  void qc.invalidateQueries({ queryKey: ['warehouse'] })
}
export function useImportPreview() {
  return useMutation({ mutationFn: (input: { text: string } | { file: File }) => {
    if ('file' in input) { const form = new FormData(); form.append('file', input.file); return apiUpload<InventurImportPreview>('/warehouse/inventur/import-preview/', form) }
    return apiFetch<InventurImportPreview>('/warehouse/inventur/import-preview/', { method: 'POST', body: JSON.stringify(input) })
  } })
}
export function useFreigabe(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { merchant_sku: string; idempotency_key: string; expected: InventurFreigabeExpected }) => apiFetch<{ duplicate: boolean; entscheidungen: InventurDecision[] }>(
      `/warehouse/inventur/${id}/freigabe/`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => { invalidateInventur(qc, id); void qc.invalidateQueries({ queryKey: SESSIONS_KEY }) },
  })
}

export interface InventurDiffRow {
  stockItemId: number; merchantSku: string | null; itemName: string | null
  gezaehlt: number; offeneVerkaufsmengen: number; vergleichswert: number
  drStand: number; echteDifferenz: number; vollstaendig: boolean; grosseDifferenz: boolean
  /** OI-116 Stufe 2 — zusaetzlicher Vergleich gegen das fuehrende Handregal-Register.
   *  Er ersetzt die Spalten darueber NICHT (die bleiben Palette+Handregal gegen DR).
   *  `null` heisst jeweils "nicht bekannt": kein Handregal gezaehlt bzw. kein Fach
   *  registriert — ausdruecklich NICHT die Menge 0. Eine registrierte 0 kommt als 0. */
  handregalGezaehlt: number | null
  handregalRegisterStand: number | null
  handregalDifferenz: number | null
}

export interface InventurMovedRow {
  moveId: number; action: string; placeBarcode: string; createdAt: string; note: string | null
}

export interface InventurDetail {
  session: InventurSession
  progress: { total: number; counted: number }
  openPlaces: InventurPlace[]
  counts: InventurCountRow[]
  diffs: InventurDiffRow[]
  moved_after_count: InventurMovedRow[]
}

export interface CountPlaceInfo {
  place: InventurPlace
  inScope: boolean
  alreadyCounted: boolean
  hasRegisteredPallets: boolean
  positions: Array<{
    positionId: number; paletteNo: string | null; merchantSku: string | null
    itemName: string | null; state: string; expectedQty: number | null; isMisch: boolean
    inScope: boolean; einheit: 'm' | 'stueck'
  }>
}

export type CountInput = {
  place_barcode?: string
  entries?: Array<{ position_id?: number; confirmed?: boolean; counted_qty?: number; empty?: boolean; count_type?: 'nicht_gefunden' }>
  handregal?: { merchant_sku: string; counted_qty: number }
  note?: string
  device_label?: string
  // A-C6/C-I7 (Welle S2): Pflichtfeld beim Backend seit `inventurCountSchema` —
  // die aufrufende Seite soll EINEN stabilen Key je logischem Sende-Versuch
  // mitgeben (Retry-Sicherheit); ist keiner gesetzt, generiert die mutationFn
  // unten einen Fallback (siehe `useRecordCount`).
  idempotency_key?: string
}

const SESSIONS_KEY = ['inventur', 'sessions'] as const
const detailKey = (id: number | null) => ['inventur', 'detail', id] as const

export function useInventurSessions() {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: [...SESSIONS_KEY, scope.key],
    queryFn: () => apiFetch<{ sessions: InventurSession[] }>('/warehouse/inventur/').then((r) => r.sessions),
    refetchInterval: 30_000,
  })
}

export function useCreateInventur() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { mode: InventurMode; scope: InventurScope; note?: string }) =>
      apiFetch<{ id: number }>('/warehouse/inventur/', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: SESSIONS_KEY }),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Anlegen fehlgeschlagen'),
  })
}

export function useInventurDetail(id: number | null) {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: [...detailKey(id), scope.key],
    queryFn: () => apiFetch<InventurDetail>(`/warehouse/inventur/${id}/`),
    enabled: id !== null,
    refetchInterval: 15_000,
  })
}

export function useRecount(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (target: { position_id: number } | { place_id: number }) =>
      apiFetch<Record<string, unknown>>(`/warehouse/inventur/${id}/recount/`, {
        method: 'POST', body: JSON.stringify(target),
      }),
    onSuccess: () => invalidateInventur(qc, id),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Zweitzählung fehlgeschlagen'),
  })
}

// A-I9/C-I6 (Welle S2): Abschluss kann jetzt mit einem Unvollstaendig-Gate
// abgelehnt werden (kein Write) — der Aufrufer zeigt dann einen Bestaetigungs-
// dialog und ruft mit confirmUnvollstaendig=true erneut auf.
// RA-BC-B (Re-Audit Runde 1, Welle W2-BE): zusaetzliche Kennzahl doppelte_positionen —
// Positionen, die im offenen Zeitraum auf mehr als einem Platz gezaehlt wurden
// (z.B. nach einem confirm_inventur-Move zwischen zwei Zaehlungen). Blockt den
// Abschluss wie offene Plaetze/Zweitzaehlungen.
// OI-116: `handregal_abgeglichen` = Anzahl der Regal-FAECHER, die dieser Abschluss in
// das fuehrende Register `warehouse_handregal_bestand` uebernommen hat. Die Zahl ist
// von `korrekturen` (Paletten-Mengen) UNABHAENGIG — eine Session kann 0 Korrekturen und
// trotzdem geaenderten Regalbestand haben. Optional getypt, weil aeltere Backend-Staende
// das Feld nicht senden.
export type CloseInventurResult =
  | { blocked: true; code: 'recapture_reserved'; message: string }
  | { closed: true; korrekturen: number; klaerfaelle: number; handregal_abgeglichen?: number; handregal_veraltet?: number }
  | {
    blocked: true; code: 'inventur_unvollstaendig'; message: string
    offene_plaetze: number; offene_zweitzaehlungen: number; korrekturen_geplant: number
    doppelte_positionen: number
    offene_paletten: number; nicht_zaehlbare_paletten: number; offene_handregalfaecher: number
  }

export function useCloseInventur(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (confirmUnvollstaendig?: boolean) =>
      apiFetch<CloseInventurResult>(`/warehouse/inventur/${id}/close/`, {
        method: 'POST',
        body: JSON.stringify({ confirm_unvollstaendig: confirmUnvollstaendig === true }),
      }),
    onSuccess: (r) => {
      if ('closed' in r) {
        invalidateInventur(qc, id)
        void qc.invalidateQueries({ queryKey: SESSIONS_KEY })
      }
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Abschluss fehlgeschlagen'),
  })
}

/** Platz-Info fuer die Zaehl-Maske (Task 9) — lazy per Barcode-Scan. */
export function fetchPlaceInfo(id: number, barcode: string): Promise<CountPlaceInfo> {
  return apiFetch<CountPlaceInfo>(`/warehouse/inventur/${id}/place/?barcode=${encodeURIComponent(barcode)}`)
}
export function useRecordCount(id: number) {
  const qc = useQueryClient()
  return useMutation({
    // A-C6/C-I7 (Welle S2): idempotency_key ist beim Backend jetzt Pflicht —
    // payload-build-time-Fallback hier (analog `sendMove` in api/warehouse.ts),
    // falls der Aufrufer keinen mitgibt. Die Zaehl-Maske (InventurZaehlenPage)
    // haelt den Key selbst pro logischem Sende-Versuch stabil, damit ein Retry
    // NICHT als zweite Zeile gespeichert wird.
    mutationFn: (body: CountInput) => {
      const payload: CountInput = { ...body, idempotency_key: body.idempotency_key ?? newIdempotencyKey() }
      return apiFetch<{ saved: number; isRecount: boolean; duplicate?: boolean }>(
        `/warehouse/inventur/${id}/count/`, { method: 'POST', body: JSON.stringify(payload) })
    },
    onSuccess: () => invalidateInventur(qc, id),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Zählung fehlgeschlagen'),
  })
}
